import React, { useState, useEffect } from 'react';
import { 
  getLocations, 
  getEmployees,
  getWeekShifts,
  getAssignmentsByShift
} from '../api';

function ControlCenter() {
  const [activeTab, setActiveTab] = useState('reports');
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  
  // Reports tab state
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [reportData, setReportData] = useState(null);
  const [editingReport, setEditingReport] = useState({
    reservations: '',
    total_cars: '',
    free_cars: '',
    paid_cars: '',
    cars_on_lot: '',
    cash_collected: '',
    card_collected: '',
    venmo_collected: '',
    is_final: false
  });

  // Schedule email tab state
  const [scheduleWeekStart, setScheduleWeekStart] = useState(getMonday(new Date()));
  const [schedulePreview, setSchedulePreview] = useState([]);
  const [sendingSchedule, setSendingSchedule] = useState(false);

  // Announcement tab state
  const [announcementSubject, setAnnouncementSubject] = useState('');
  const [announcementMessage, setAnnouncementMessage] = useState('');
  const [sendingAnnouncement, setSendingAnnouncement] = useState(false);

  // Get tenant_id from logged-in user
const user = JSON.parse(localStorage.getItem('user') || '{}');
const tenantId = user.tenant_id || 1;

  function getMonday(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    return d.toISOString().split('T')[0];
  }

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInitialData = async () => {
    try {
      const [locRes, empRes] = await Promise.all([
        getLocations(tenantId),
        getEmployees(tenantId)
      ]);
      const activeLocations = locRes.data.filter(l => l.active && !l.scheduling_only);
      setLocations(activeLocations);
      setEmployees(empRes.data.filter(e => e.active));
      if (activeLocations.length > 0) {
        setSelectedLocation(activeLocations[0].id);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchReportForDateLocation = async () => {
    if (!selectedDate || !selectedLocation) return;
    
    try {
      const response = await fetch(
        `http://localhost:3000/api/reports/date/${selectedDate}/location/${selectedLocation}`
      );
      if (response.ok) {
        const data = await response.json();
        setReportData(data);
        if (data) {
          setEditingReport({
            reservations: data.reservations || '',
            total_cars: data.total_cars || '',
            free_cars: data.free_cars || '',
            paid_cars: data.paid_cars || '',
            cars_on_lot: data.cars_on_lot || '',
            cash_collected: data.cash_collected || '',
            card_collected: data.card_collected || '',
            venmo_collected: data.venmo_collected || '',
            is_final: data.is_final || false
          });
        } else {
          setEditingReport({
            reservations: '',
            total_cars: '',
            free_cars: '',
            paid_cars: '',
            cars_on_lot: '',
            cash_collected: '',
            card_collected: '',
            venmo_collected: '',
            is_final: false
          });
        }
      }
    } catch (err) {
      console.error('Failed to fetch report:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'reports') {
      fetchReportForDateLocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedLocation, activeTab]);

  const handleSaveReport = async () => {
    try {
      const response = await fetch(
        `http://localhost:3000/api/reports/date/${selectedDate}/location/${selectedLocation}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            reservations: parseInt(editingReport.reservations) || 0,
            total_cars: parseInt(editingReport.total_cars) || 0,
            free_cars: parseInt(editingReport.free_cars) || 0,
            paid_cars: parseInt(editingReport.paid_cars) || 0,
            cars_on_lot: parseInt(editingReport.cars_on_lot) || 0,
            cash_collected: parseFloat(editingReport.cash_collected) || 0,
            card_collected: parseFloat(editingReport.card_collected) || 0,
            venmo_collected: parseFloat(editingReport.venmo_collected) || 0,
            is_final: editingReport.is_final
          })
        }
      );
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Report saved successfully!' });
        fetchReportForDateLocation();
      } else {
        setMessage({ type: 'error', text: 'Failed to save report' });
      }
    } catch (err) {
      console.error('Failed to save report:', err);
      setMessage({ type: 'error', text: 'Failed to save report' });
    }
    
    setTimeout(() => setMessage(null), 3000);
  };

  const fetchSchedulePreview = async () => {
    try {
      const shiftRes = await getWeekShifts(tenantId, scheduleWeekStart);
      const shifts = shiftRes.data;
      
      const scheduleData = [];
      for (const shift of shifts) {
        const assignRes = await getAssignmentsByShift(shift.id);
        const location = locations.find(l => l.id === shift.location_id);
        for (const assignment of assignRes.data) {
          const emp = employees.find(e => e.id === assignment.employee_id);
          if (emp) {
            scheduleData.push({
              employee: emp,
              location: location?.name || 'Unknown',
              date: shift.shift_date,
              start_time: assignment.scheduled_start,
              end_time: assignment.scheduled_end
            });
          }
        }
      }
      setSchedulePreview(scheduleData);
    } catch (err) {
      console.error('Failed to fetch schedule:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'schedule') {
      fetchSchedulePreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleWeekStart, activeTab]);

  const handleSendSchedule = async () => {
    if (schedulePreview.length === 0) {
      setMessage({ type: 'error', text: 'No schedule to send' });
      return;
    }
    
    setSendingSchedule(true);
    try {
      const response = await fetch('http://localhost:3000/api/email/send-schedule', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          week_start: scheduleWeekStart,
          tenant_id: tenantId
        })
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Schedule sent to all employees!' });
      } else {
        setMessage({ type: 'error', text: 'Failed to send schedule. Email may not be configured.' });
      }
    } catch (err) {
      console.error('Failed to send schedule:', err);
      setMessage({ type: 'error', text: 'Failed to send schedule. Email may not be configured.' });
    } finally {
      setSendingSchedule(false);
    }
    
    setTimeout(() => setMessage(null), 3000);
  };

  const handleSendAnnouncement = async () => {
    if (!announcementSubject.trim() || !announcementMessage.trim()) {
      setMessage({ type: 'error', text: 'Please enter a subject and message' });
      return;
    }
    
    setSendingAnnouncement(true);
    try {
      const response = await fetch('http://localhost:3000/api/email/send-announcement', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: announcementSubject,
          message: announcementMessage,
          tenant_id: tenantId
        })
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Announcement sent to all employees!' });
        setAnnouncementSubject('');
        setAnnouncementMessage('');
      } else {
        setMessage({ type: 'error', text: 'Failed to send announcement. Email may not be configured.' });
      }
    } catch (err) {
      console.error('Failed to send announcement:', err);
      setMessage({ type: 'error', text: 'Failed to send announcement. Email may not be configured.' });
    } finally {
      setSendingAnnouncement(false);
    }
    
    setTimeout(() => setMessage(null), 3000);
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'pm' : 'am';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes}${ampm}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const tabs = [
    { id: 'reports', label: '📊 Edit Reports' },
    { id: 'schedule', label: '📅 Send Schedule' },
    { id: 'announcement', label: '📢 Announcements' }
  ];

  if (loading) {
    return (
      <div style={{ padding: '48px' }}>
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '48px' }}>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Control Center</h1>
        <p className="text-gray-600 mt-1">Manage reports, send schedules, and communicate with your team</p>
      </div>

      {/* Message */}
      {message && (
        <div style={{
          padding: '16px',
          borderRadius: '8px',
          marginBottom: '24px',
          backgroundColor: message.type === 'success' ? '#dcfce7' : '#fee2e2',
          color: message.type === 'success' ? '#166534' : '#dc2626',
          fontWeight: '500'
        }}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: '0'
      }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '12px 24px',
              backgroundColor: activeTab === tab.id ? '#ffffff' : 'transparent',
              border: activeTab === tab.id ? '2px solid #e5e7eb' : '2px solid transparent',
              borderBottom: activeTab === tab.id ? '2px solid #ffffff' : '2px solid transparent',
              marginBottom: '-2px',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontWeight: activeTab === tab.id ? '600' : '500',
              color: activeTab === tab.id ? '#3b82f6' : '#6b7280',
              fontSize: '14px'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        padding: '24px'
      }}>
        {/* Edit Reports Tab */}
        {activeTab === 'reports' && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Edit Daily Report</h2>
            
            {/* Date and Location Selectors */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Date
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Location
                </label>
                <select
                  value={selectedLocation || ''}
                  onChange={(e) => setSelectedLocation(parseInt(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    boxSizing: 'border-box',
                    backgroundColor: '#ffffff'
                  }}
                >
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Report Form */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(4, 1fr)', 
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Reservations
                </label>
                <input
                  type="number"
                  value={editingReport.reservations}
                  onChange={(e) => setEditingReport({...editingReport, reservations: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Total Cars
                </label>
                <input
                  type="number"
                  value={editingReport.total_cars}
                  onChange={(e) => setEditingReport({...editingReport, total_cars: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Free Cars
                </label>
                <input
                  type="number"
                  value={editingReport.free_cars}
                  onChange={(e) => setEditingReport({...editingReport, free_cars: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Paid Cars
                </label>
                <input
                  type="number"
                  value={editingReport.paid_cars}
                  onChange={(e) => setEditingReport({...editingReport, paid_cars: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Cars on Lot
                </label>
                <input
                  type="number"
                  value={editingReport.cars_on_lot}
                  onChange={(e) => setEditingReport({...editingReport, cars_on_lot: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Cash Collected ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={editingReport.cash_collected}
                  onChange={(e) => setEditingReport({...editingReport, cash_collected: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Card Collected ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={editingReport.card_collected}
                  onChange={(e) => setEditingReport({...editingReport, card_collected: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Venmo Collected ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={editingReport.venmo_collected}
                  onChange={(e) => setEditingReport({...editingReport, venmo_collected: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            {/* Final Report Checkbox */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px', 
              marginBottom: '24px',
              padding: '16px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px'
            }}>
              <input
                type="checkbox"
                id="is_final"
                checked={editingReport.is_final}
                onChange={(e) => setEditingReport({...editingReport, is_final: e.target.checked})}
                style={{ width: '20px', height: '20px' }}
              />
              <label htmlFor="is_final" style={{ fontSize: '14px', color: '#374151' }}>
                <strong>Mark as Final Report</strong> — Check this to include in KPI calculations
              </label>
            </div>

            <button
              onClick={handleSaveReport}
              style={{
                backgroundColor: '#3b82f6',
                color: '#ffffff',
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              Save Report
            </button>
          </div>
        )}

        {/* Send Schedule Tab */}
        {activeTab === 'schedule' && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Send Weekly Schedule</h2>
            
            {/* Week Selector */}
            <div style={{ marginBottom: '24px', maxWidth: '300px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                Select Week
              </label>
              <input
                type="date"
                value={scheduleWeekStart}
                onChange={(e) => setScheduleWeekStart(getMonday(new Date(e.target.value)))}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  boxSizing: 'border-box'
                }}
              />
              <p className="text-sm text-gray-500 mt-1">
                Week of {new Date(scheduleWeekStart).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            </div>

            {/* Schedule Preview */}
            <div style={{ marginBottom: '24px' }}>
              <h3 className="font-semibold text-gray-900 mb-3">Preview ({schedulePreview.length} assignments)</h3>
              {schedulePreview.length > 0 ? (
                <div style={{ 
                  maxHeight: '300px', 
                  overflowY: 'auto',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f9fafb' }}>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Employee</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Location</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Date</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedulePreview.map((item, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '12px' }}>{item.employee.first_name} {item.employee.last_name}</td>
                          <td style={{ padding: '12px' }}>{item.location}</td>
                          <td style={{ padding: '12px' }}>{formatDate(item.date)}</td>
                          <td style={{ padding: '12px' }}>
                            {item.start_time ? formatTime(item.start_time) : '—'}
                            {item.end_time ? ` - ${formatTime(item.end_time)}` : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500">No assignments for this week</p>
              )}
            </div>

            <button
              onClick={handleSendSchedule}
              disabled={sendingSchedule || schedulePreview.length === 0}
              style={{
                backgroundColor: schedulePreview.length === 0 ? '#d1d5db' : '#22c55e',
                color: '#ffffff',
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                cursor: schedulePreview.length === 0 ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              {sendingSchedule ? 'Sending...' : 'Send Schedule to All Employees'}
            </button>
            
            <p className="text-sm text-gray-500 mt-3">
              This will send an email to all {employees.length} active employees with the full weekly schedule.
            </p>
          </div>
        )}

        {/* Announcements Tab */}
        {activeTab === 'announcement' && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Send Company Announcement</h2>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                Subject
              </label>
              <input
                type="text"
                value={announcementSubject}
                onChange={(e) => setAnnouncementSubject(e.target.value)}
                placeholder="e.g., Schedule Update, Company News, Reminder"
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                Message
              </label>
              <textarea
                value={announcementMessage}
                onChange={(e) => setAnnouncementMessage(e.target.value)}
                placeholder="Type your message here..."
                rows={6}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  boxSizing: 'border-box',
                  resize: 'vertical'
                }}
              />
            </div>

            <button
              onClick={handleSendAnnouncement}
              disabled={sendingAnnouncement || !announcementSubject.trim() || !announcementMessage.trim()}
              style={{
                backgroundColor: (!announcementSubject.trim() || !announcementMessage.trim()) ? '#d1d5db' : '#f59e0b',
                color: '#ffffff',
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                cursor: (!announcementSubject.trim() || !announcementMessage.trim()) ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              {sendingAnnouncement ? 'Sending...' : 'Send to All Employees'}
            </button>
            
            <p className="text-sm text-gray-500 mt-3">
              This will send an email to all {employees.length} active employees.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ControlCenter;